import type { CSSProperties, ReactNode } from "react";
import { TriangleAlert } from "lucide-react";
import {
  Toaster as SonnerToaster,
  toast as sonnerToast,
  type ToasterProps,
} from "sonner";
import { Toast } from "./Toast";
import type { ToastOptions, ToastSentiment } from "./types";

/**
 * Global toast host. Wraps sonner's `<Toaster>` so every toast renders the DS
 * `Toast` card (sonner still owns positioning, stacking, and auto-dismiss).
 * Defaults to bottom-left; all sonner props pass through.
 */
export const Toaster = ({
  position = "bottom-left",
  style,
  toastOptions,
  ...props
}: ToasterProps) => (
  <SonnerToaster
    position={position}
    style={{ "--width": "360px", ...style } as CSSProperties}
    toastOptions={{ unstyled: true, ...toastOptions }}
    {...props}
  />
);

const showToast = (
  sentiment: ToastSentiment,
  message: ReactNode,
  { icon, actions, description, ...options }: ToastOptions = {},
) =>
  sonnerToast.custom(
    id => (
      <Toast
        sentiment={sentiment}
        title={message}
        description={description}
        icon={icon}
        actions={actions}
        onClose={() => sonnerToast.dismiss(id)}
      />
    ),
    options,
  );

/**
 * Drop-in replacement for sonner's `toast`: same call signatures, but each
 * helper renders the DS `Toast` card. `warning` uses neutral styling with a
 * `TriangleAlert` icon (the design system has no warning sentiment for panels).
 */
export const toast = Object.assign(
  (message: ReactNode, options?: ToastOptions) =>
    showToast("default", message, options),
  {
    success: (message: ReactNode, options?: ToastOptions) =>
      showToast("positive", message, options),
    error: (message: ReactNode, options?: ToastOptions) =>
      showToast("negative", message, options),
    info: (message: ReactNode, options?: ToastOptions) =>
      showToast("emphasis", message, options),
    warning: (message: ReactNode, options?: ToastOptions) =>
      showToast("default", message, { icon: TriangleAlert, ...options }),
    message: (message: ReactNode, options?: ToastOptions) =>
      showToast("default", message, options),
    dismiss: sonnerToast.dismiss,
    custom: sonnerToast.custom,
  },
);
